'use client';

import { ReactNode, Suspense } from 'react';

import { Map } from 'mapbox-gl';

import { AnalyticsProvider } from '../../contexts/AnalyticsContext/AnalyticsContext';
import { AuthProvider } from '../../contexts/AuthContext';
import { CookiePermissionsProvider } from '../../contexts/CookiePermissionsContext/CookiePermissionsContext';
import { GeolocationProvider } from '../../contexts/GeolocationContext/GeolocationContext';
import { SessionProvider } from '../../contexts/SessionContext';
import { _hasAccessToken } from '../../helpers/authentication/shared';
import { hasCookies, initialiseCookies } from '../../helpers/cookies';
import { IEvent } from '../../interfaces/events';
import { ApolloWrapper } from '../ApolloWrapper/ApolloWrapper';
import { ClientSideInitializer } from '../ClientSideInitializer/ClientSideInitializer';
import { PageViewHandler } from '../PageViewHandler/PageViewHandler';
import { Facebook } from '../Scripts/Facebook/Facebook';
import { GoogleTagManager } from '../Scripts/GoogleTagManager/GoogleTagManager';
import { Hotjar } from '../Scripts/Hotjar/Hotjar';
import { getReactErrorBoundary } from '../../helpers/errorHandling/client';

/**
 * The ClientAppLogic component is responsible for rendering the
 * client-side logic of the application. It wraps the entire
 * application with various context providers and handles error boundaries.
 *
 * Can be wrapped by the LayoutWrapper which is a server component
 * or can be used without the layout as in the case of app pages.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {ReactNode} [props.children] - The child components to be rendered.
 * @param {boolean} [props.blockDatadogRum=false] - Whether to block Datadog RUM.
 * @returns {JSX.Element} The rendered component.
 */

interface IProps {
  children?: ReactNode;
}

declare global {
  interface Window {
    __APOLLO_STATE__: object;
    __BUILDER_APOLLO_STATE__: object;
    __BUILDER_IO_APOLLO_STATE__: object;
    __HAS_AUTH_TOKEN: boolean;
    __PENDING_EVENTS__: IEvent[];
    Cypress: unknown;
    dataLayer?: object[];
    exploreMap?: Map;
    fbq?: (...args: any) => void; // Facebook pixel
    gapi: any;
    gtag?: (...args: any) => void; // Google Tag Manager
    Helpshift?: (...args: any) => void; // SupportChat
    Osano?: any; // Cookie consent
    zE?: (...args: any) => void; // Zendesk
  }
}

// Initialise cookies. Not in a useEffect hook as we need it before the first render
// NB: For server components this will not instantiate the cookies
if (!hasCookies()) {
  initialiseCookies();
}

const ErrorBoundary = getReactErrorBoundary();

export const ClientAppLogic = ({ children }: IProps) => {
  return (
    <ErrorBoundary>
      <GeolocationProvider>
        <CookiePermissionsProvider>
          <Suspense>
            <GoogleTagManager />
          </Suspense>
          <Hotjar />
          <Facebook />
          <ApolloWrapper>
            <AnalyticsProvider>
              {/* We start off unauthenticated by default and let the authentication be updated on the client */}
              <AuthProvider isAuthenticated={_hasAccessToken()}>
                <SessionProvider>
                  <Suspense>
                    <ClientSideInitializer />
                    <PageViewHandler />
                  </Suspense>
                  {children}
                </SessionProvider>
              </AuthProvider>
            </AnalyticsProvider>
          </ApolloWrapper>
        </CookiePermissionsProvider>
      </GeolocationProvider>
    </ErrorBoundary>
  );
};
